import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface UnassignedParticipant {
  user_id: string;
  event_id: string;
  skills: string[];
  team_preferences?: string;
  availability_status: 'available' | 'in_team' | 'unavailable';
  registered_at: string;
  updated_at: string;
  // Enhanced user details for internal tool
  email?: string;
  display_name?: string;
}

interface ParticipantManagerProps {
  eventId: string;
}

const ParticipantManager: React.FC<ParticipantManagerProps> = ({ eventId }) => {
  const { token, user } = useAuth();
  const [participants, setParticipants] = useState<UnassignedParticipant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [newTeamDescription, setNewTeamDescription] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');

  // Check if user is organizer (only organizers can create teams with participants) - case-insensitive
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (eventId && token) {
      loadUnassignedParticipants();
    }
  }, [eventId, token]);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const loadUnassignedParticipants = async () => {
    if (!eventId || !token) return;

    setLoading(true);
    try {
      const response = await apiGet<UnassignedParticipant[]>(
        `/events/${eventId}/participants/unassigned`, 
        token
      );
      setParticipants(response || []);
    } catch (error) {
      console.error('Error loading unassigned participants:', error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantSelection = (userId: string, selected: boolean) => {
    const newSelected = new Set(selectedParticipants);
    if (selected) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedParticipants(newSelected);
  };

  const createTeamWithParticipants = async () => {
    if (!newTeamName.trim() || selectedParticipants.size === 0 || !token) {
      showMessage('Please provide a team name and select at least one participant', 'error');
      return;
    }

    setCreating(true);
    try {
      // First create the team
      const teamData = await apiPost<{ team_id: string }>('/teams', {
        event_id: eventId,
        name: newTeamName.trim(),
        description: newTeamDescription.trim() || undefined,
        looking_for_members: false
      }, token);

      // Then add selected participants to the team
      const participantUserIds = Array.from(selectedParticipants);
      for (const userId of participantUserIds) {
        // Find the participant data to get email and display_name
        const participant = participants.find(p => p.user_id === userId);
        if (!participant) {
          console.error(`Participant not found for user_id: ${userId}`);
          continue;
        }

        await apiPost(`/teams/${teamData.team_id}/members`, {
          user_id: userId,
          email: participant.email || '',
          display_name: participant.display_name || participant.email || 'Unknown',
          role: 'member'
        }, token);
      }

      showMessage(
        `Successfully created team "${newTeamName}" with ${participantUserIds.length} members`, 
        'success'
      );
      
      // Reset form
      setNewTeamName('');
      setNewTeamDescription('');
      setSelectedParticipants(new Set());
      
      // Reload participants
      loadUnassignedParticipants();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create team';
      showMessage(errorMessage, 'error');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '20px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        marginTop: '20px'
      }}>
        <div className="loading">Loading unassigned participants...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      marginTop: '20px'
    }}>
      <h3 style={{ marginTop: '0', marginBottom: '15px', color: '#333' }}>
        � Available Participants {isOrganizer ? '(Team Management)' : '(Team Discovery)'}
      </h3>

      {message && (
        <div style={{
          padding: '10px',
          marginBottom: '15px',
          borderRadius: '4px',
          backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
          color: messageType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${messageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <h4>📊 Unassigned Participants ({participants.length})</h4>
        <p style={{ color: '#666', fontSize: '14px', margin: '5px 0' }}>
          {isOrganizer 
            ? 'These participants have registered for the event but are not yet part of a team.'
            : 'These participants are looking for teams. You can see their skills and preferences to form teams together.'
          }
        </p>
      </div>

      {participants.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: '#ffffff', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h4>✅ All participants are assigned to teams</h4>
          <p style={{ color: '#666' }}>
            Great! Every registered participant is already part of a team.
          </p>
        </div>
      ) : (
        <>
          {/* Participant List */}
          <div style={{ 
            marginBottom: '30px',
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            {participants.map((participant) => (
              <div 
                key={participant.user_id}
                style={{
                  padding: '15px',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '15px'
                }}
              >
                {isOrganizer && (
                  <input
                    type="checkbox"
                    checked={selectedParticipants.has(participant.user_id)}
                    onChange={(e) => handleParticipantSelection(participant.user_id, e.target.checked)}
                    style={{ marginTop: '5px' }}
                  />
                )}
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                    <strong>
                      {participant.display_name || participant.email || `Participant`}
                    </strong>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {participant.email && participant.display_name ? `(${participant.email})` : `(ID: ${participant.user_id.substring(0, 8)}...)`}
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: participant.availability_status === 'available' ? '#d4edda' : '#f8d7da',
                      color: participant.availability_status === 'available' ? '#155724' : '#721c24'
                    }}>
                      {participant.availability_status}
                    </span>
                  </div>
                  
                  {participant.skills.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                        Skills:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {participant.skills.map((skill) => (
                          <span 
                            key={`${participant.user_id}-${skill}`}
                            style={{
                              padding: '2px 8px',
                              backgroundColor: '#e7f3ff',
                              color: '#0056b3',
                              borderRadius: '12px',
                              fontSize: '11px',
                              border: '1px solid #b3d7ff'
                            }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {participant.team_preferences && (
                    <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                      "{participant.team_preferences}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Team Creation Form - Only for Organizers */}
          {isOrganizer && (
            <div style={{
              padding: '20px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginTop: '0', marginBottom: '15px' }}>
                🏗️ Create Team with Selected Participants ({selectedParticipants.size} selected)
              </h4>
              
              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="team-name" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Team Name:
                </label>
                <input
                  id="team-name"
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Enter team name..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="team-description" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Team Description (Optional):
                </label>
                <textarea
                  id="team-description"
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  placeholder="Describe the team or project focus..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <button
                onClick={createTeamWithParticipants}
                disabled={creating || selectedParticipants.size === 0 || !newTeamName.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: creating || selectedParticipants.size === 0 || !newTeamName.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: creating || selectedParticipants.size === 0 || !newTeamName.trim() ? 0.6 : 1
                }}
              >
                {creating ? '⏳ Creating Team...' : `🏗️ Create Team with ${selectedParticipants.size} Members`}
              </button>
            </div>
          )}

          {/* Information for non-organizers */}
          {!isOrganizer && participants.length > 0 && (
            <div style={{
              padding: '15px',
              backgroundColor: '#e7f3ff',
              borderRadius: '8px',
              border: '1px solid #b3d7ff',
              marginTop: '20px'
            }}>
              <h4 style={{ marginTop: '0', marginBottom: '10px', color: '#0056b3' }}>
                💡 Team Formation Tips
              </h4>
              <p style={{ margin: '0', fontSize: '14px', color: '#004494' }}>
                These participants are available to join teams. You can reach out to them directly to discuss collaboration opportunities, 
                or create your own team and invite them to join. Look for complementary skills that match your project needs!
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ParticipantManager;
